import React from 'react';
import { BookOpenIcon, CreditCardIcon, LockKeyholeIcon, PrinterIcon, ShieldCheckIcon, SparklesIcon, ZapIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { CTAButton, PRICE } from './Shared';
import { Bamboo, KanjiBackdrop, Mandala, Needle, YinYang } from './Ornaments';
const productMockupUrl = 'https://cdn.magicpatterns.com/uploads/nrdXXmUDMkNvVUETEWbSe7/pasted-image.png';
export function Hero() {
  const reduceMotion = useReducedMotion();
  return <header className="relative isolate overflow-hidden pb-6 pt-1.5 sm:pb-8 sm:pt-7 md:pb-14 md:pt-14" data-id="element-89" data-mp="xbflbcukdj6p">
      <KanjiBackdrop glyphs={['針', '灸', '氣', '陰', '陽']} data-id="element-90" data-mp="sa9slf0wlvx6" />
      <Mandala className="pointer-events-none absolute -right-24 top-2 -z-10 h-72 w-72 opacity-[0.13] md:right-4 md:h-96 md:w-96" color="#A98546" data-id="element-91" data-mp="nc87kojx3mj2" />
      <Mandala className="pointer-events-none absolute -bottom-28 -left-28 -z-10 h-80 w-80 opacity-[0.1] md:h-[30rem] md:w-[30rem]" color="#4A6B57" data-id="element-92" data-mp="3d2tpxe128sgn" />
      <Bamboo className="absolute left-[6%] top-52 hidden h-16 w-16 opacity-40 md:block" color="#4A6B57" data-id="element-93" data-mp="3j9u1r1hjpsmv" />
      <Needle className="absolute right-[6%] top-60 hidden h-14 w-14 opacity-40 md:block" color="#7C5F2A" data-id="element-94" data-mp="3o5j4tesrxrnx" />

      <div className="mx-auto max-w-7xl px-4 text-center sm:px-6 lg:px-8" data-id="element-95" data-mp="3e95zfjnv9pdc">
        <motion.div initial={reduceMotion ? false : {
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-96" data-mp="3ebioegs1kzzw">
          <div className="mb-1.5 inline-flex items-center gap-2 rounded-sm border border-brand-gold/60 bg-brand-paper px-2.5 py-0.5 shadow-sm sm:mb-5 sm:px-4 sm:py-1.5" data-id="element-97" data-mp="2vj7tb93mq6cc">
            <YinYang className="h-3 w-3 sm:h-4 sm:w-4" color="#4A6B57" data-id="element-98" data-mp="2ljpaivzggqlv" />
            <span className="font-serif text-[9px] font-semibold tracking-[0.22em] text-brand-ink sm:text-xs" data-id="element-99" data-mp="s1rsao8pe6ua">
              LUMEN AKASHA™
            </span>
          </div>

          <p className="font-kanji text-sm text-brand-goldDark/80 sm:mb-1 sm:text-2xl" data-id="element-100" data-mp="n9i3xcn8leu4">
            針灸
          </p>
          <p className="mb-1 font-serif text-[11px] font-semibold tracking-[0.14em] text-brand-forest sm:mb-4 sm:text-base" data-id="element-101" data-mp="dd59gcjvawyb">
            Atlas Ancestral de Acupuntura™
          </p>
          <h1 className="mx-auto max-w-5xl font-serif text-[32px] font-semibold leading-[1.1] text-brand-ink sm:text-6xl md:text-[4.5rem]" data-id="element-102" data-mp="1ayjilhmnxm7u">
            ¿Trabajas con{' '}
            <span className="font-bold text-brand-cinnabar" data-id="element-103" data-mp="11152jj4nizg2">Acupuntura</span> o{' '}
            <span className="font-bold text-brand-cinnabar" data-id="element-104" data-mp="x0crckc7ykh5">
              Medicina China?
            </span>
          </h1>
          <p className="mx-auto mt-2 max-w-xl text-[17px] leading-snug text-brand-text sm:mt-6 sm:max-w-4xl sm:text-2xl sm:leading-relaxed md:text-3xl" data-id="element-105" data-mp="3lu9yf4ila4ct">
            Ten siempre a mano el punto exacto, el meridiano y el protocolo que
            necesitas con una guía visual clínica, práctica y fácil de
            consultar.
          </p>

          <div className="relative mx-auto mt-3 w-full max-w-[22rem] sm:mt-10 sm:max-w-md md:max-w-lg" data-id="element-106" data-mp="26a96xcjm0wrz">
            <motion.img src={productMockupUrl} alt="Mockup del Atlas Ancestral de Acupuntura con los 3 tomos y los 6 bonos de regalo" initial={reduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: reduceMotion ? 0 : 0.12,
            ease: [0.22, 1, 0.36, 1]
          }} className="mockup-blend mx-auto h-auto max-h-[46svh] w-full object-contain sm:max-h-[36rem]" draggable={false} decoding="async" data-id="element-107" data-mp="2b197qsvunyzr" />
          </div>

          <div className="relative z-10 mx-auto mt-1 flex max-w-xl flex-wrap justify-center gap-x-2.5 gap-y-1 text-[10px] font-medium text-brand-forest sm:mt-4 sm:text-xs" data-id="element-108" data-mp="3vgvumkzgh32k">
            <span data-id="element-109" data-mp="9a7c9dt1marf">📚 3 tomos</span>
            <span data-id="element-110" data-mp="9b0ec1h24c38">🎁 6 bonos gratis</span>
            <span data-id="element-111" data-mp="3hq0ubx30vual">📄 100% PDF</span>
            <span className="hidden sm:inline" data-id="element-112" data-mp="3hrbu7ltckhwf">
              🖨️ Listo para imprimir o usar en tablet
            </span>
          </div>

          <div className="mt-3 flex justify-center sm:hidden" data-id="element-113" data-mp="3cp33kw3y5rxh">
            <CTAButton className="w-full max-w-[22rem] px-4 py-3 text-[12.5px] leading-tight" data-id="element-114" data-mp="3rgvhr4s08zxm">
              → QUIERO MI ATLAS ANCESTRAL {PRICE}
            </CTAButton>
          </div>

          <div className="mt-5 hidden flex-col items-center gap-5 sm:flex" data-id="element-115" data-mp="2yyznynzkowh0">
            <CTAButton className="px-7 py-4 text-sm md:px-10 md:text-lg" data-id="element-116" data-mp="2u0y07pr7c8z7">
              → QUIERO MI ATLAS ANCESTRAL {PRICE}
            </CTAButton>
            <div className="flex max-w-3xl flex-wrap justify-center gap-x-4 gap-y-2 text-xs text-brand-muted md:text-sm" data-id="element-117" data-mp="19h2r6kuc7o5z">
              <span className="inline-flex items-center gap-1.5" data-id="element-118" data-mp="1ebglejx9jp92">
                <ShieldCheckIcon className="h-4 w-4 text-brand-forest" data-id="element-119" data-mp="zlsk3ip30qlc" />
                Garantía 7 días
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-120" data-mp="14hhqa4vlx5qm">
                <PrinterIcon className="h-4 w-4 text-brand-forest" data-id="element-121" data-mp="1sagd60g2ok9g" />
                Imprimible o para usar en tablet
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-122" data-mp="1x5vp2muis638">
                <LockKeyholeIcon className="h-4 w-4 text-brand-forest" data-id="element-123" data-mp="1j7p1q0n75oht" />
                Pago seguro
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-124" data-mp="1nc53pqtowbrr">
                <CreditCardIcon className="h-4 w-4 text-brand-forest" data-id="element-125" data-mp="7s8s129z9wsq" />
                Tarjeta o Mercado Pago
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2 text-xs font-semibold text-brand-ink md:text-sm" data-id="element-126" data-mp="bskac8evbl11">
              <span className="rounded-sm border border-brand-gold/40 bg-brand-paper px-3 py-2" data-id="element-127" data-mp="29ir25ir7oyby">
                <BookOpenIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-128" data-mp="24rhdakdhlz14" />
                100% PDF
              </span>
              <span className="rounded-sm border border-brand-gold/40 bg-brand-paper px-3 py-2" data-id="element-129" data-mp="1zrmoqnh6plj8">
                <ZapIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-130" data-mp="1vs41hl1w4yd8" />
                Acceso en 5 min
              </span>
              <span className="rounded-sm border border-brand-gold/40 bg-brand-paper px-3 py-2" data-id="element-131" data-mp="1qqoh7v7jevvw">
                <SparklesIcon className="mr-1 inline h-3.5 w-3.5 text-brand-forest" data-id="element-132" data-mp="1lt52yvauaj3y" />
                Para terapeutas de todos los niveles
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </header>;
}